import * as zod from 'zod';

import { CartesianPose } from '@sb/geometry';

export const WristCamera = zod.object({
  kind: zod.literal('wristCamera'),
  name: zod.string().default('Wrist Camera'),
  // realsense defaults
  brightness: zod.number().default(0),
  contrast: zod.number().default(50),
  exposure: zod.number().default(350),
  sharpness: zod.number().default(50),
  hue: zod.number().default(0),
  whiteBalance: zod.number().default(4600),
  autoWhiteBalance: zod.boolean().default(true),
});

export type WristCamera = zod.infer<typeof WristCamera>;

export const CameraIntegration = zod.union([
  zod.never(), // remove when we have > 1 camera type; it's only here to make a valid union
  WristCamera,
]);

export type CameraIntegration = zod.infer<typeof CameraIntegration>;

export const CameraStatus = zod.object({
  connected: zod.boolean(),
  message: zod.string(),
});

export type CameraStatus = zod.infer<typeof CameraStatus>;

export const CameraIntrinsics = zod.object({
  width: zod.number(), // pixels
  height: zod.number(), // pixels
  // Focal length
  fx: zod.number(),
  fy: zod.number(),
  // Principal point
  ppx: zod.number(),
  ppy: zod.number(),
  error: zod.number().optional(),
});

export type CameraIntrinsics = zod.infer<typeof CameraIntrinsics>;

export const CameraIntrinsicsExtended = CameraIntrinsics.extend({
  distortionCoefficients: zod.array(zod.number()).default([]),
  distortionModel: zod.string().nullable().default(null),
});

export type CameraIntrinsicsExtended = zod.infer<
  typeof CameraIntrinsicsExtended
>;

export const CameraStreamSource = zod.object({
  id: zod.string(),
  name: zod.string(),
});

export const ChessboardParameters = zod.object({
  rows: zod.number().gt(0),
  cols: zod.number().gt(0),
  squareSize: zod.number().gt(0), // specified in meters
});

export type ChessboardParameters = zod.infer<typeof ChessboardParameters>;

/**
 * Default chessboard parameters: Note that we use the convention that "cols" is number of column intersections (i.e. number of cols - 1). We are actually detecting these intersections.
 */
export const DEFAULT_CHESSBOARD_PARAMS = {
  cols: 6,
  rows: 4,
  squareSize: 0.03, // specified in meters
};

export const ExtrinsicsType = zod.enum(['base', 'tcp']);

export type ExtrinsicsType = zod.infer<typeof ExtrinsicsType>;

export type CameraStreamSource = zod.infer<typeof CameraStreamSource>;

export const CalibrationEntry = zod.object({
  wristPose: CartesianPose,
  storageID: zod.string(), // Empty string used to indicate image not captured yet
  timestamp: zod.union([
    zod.date(),
    zod.string().transform((str) => new Date(str)),
  ]),
});

const CalibrationDataEntryBase = zod.object({
  id: zod.string(),
  timestamp: zod.union([
    zod.date(),
    zod.string().transform((str) => new Date(str)),
  ]),
  name: zod.string(),
  cameraId: zod.string(),
  streamId: zod.string().nullable().default(null),
  data: CalibrationEntry.array().default([]),
  isBuiltIn: zod.boolean().default(false),
  chessboard: ChessboardParameters.default(DEFAULT_CHESSBOARD_PARAMS),
});

export const IntrinsicsCalibrationDataEntry = CalibrationDataEntryBase.extend({
  intrinsics: CameraIntrinsicsExtended.nullable().default(null),
});

export type IntrinsicsCalibrationDataEntry = zod.infer<
  typeof IntrinsicsCalibrationDataEntry
>;

export const ExtrinsicsCalibrationDataEntry = CalibrationDataEntryBase.extend({
  intrinsicsId: zod.string().nullable().default(null),
  extrinsics: CartesianPose.nullable().default(null),
  kind: ExtrinsicsType,
});

export type ExtrinsicsCalibrationDataEntry = zod.infer<
  typeof ExtrinsicsCalibrationDataEntry
>;

export const IntrinsicsCalibrationData = zod.record(
  zod.string(),
  IntrinsicsCalibrationDataEntry,
);

export type IntrinsicsCalibrationData = zod.infer<
  typeof IntrinsicsCalibrationData
>;

export const ExtrinsicsCalibrationData = zod.record(
  zod.string(),
  ExtrinsicsCalibrationDataEntry,
);

export type ExtrinsicsCalibrationData = zod.infer<
  typeof ExtrinsicsCalibrationData
>;

export const ExtrinsicsKinematics = zod.object({
  extrinsics: CartesianPose,
  kind: ExtrinsicsType,
});

export type ExtrinsicsKinematics = zod.infer<typeof ExtrinsicsKinematics>;
